// Create Account (HOP-34) + passwordless wiring (HOP-38/39).
//
// Reached after a valid invite code on /auth/signup. The grown-up creates a
// passwordless account with first name + email, then Auth0 sends a one-time
// code through the passwordless email connection.

import { useState, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Grain } from '@/components/hs/Grain'
import { INVITE_CODE_KEY } from './SignUpPage'
import { setPendingFirstName } from '@/lib/pendingProfile'
import '@/styles/auth-pages.css'

const PASSWORDLESS_CONNECTION = (import.meta.env.VITE_AUTH0_PASSWORDLESS_CONNECTION as string) || 'email'

const ARROW = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10h13M11 4l6 6-6 6" />
  </svg>
)

function CaField({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  onEnter,
}: {
  label: string
  type?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoComplete?: string
  onEnter?: () => void
}) {
  return (
    <div className="auth-field">
      <div className="auth-eyebrow auth-field__label">{label}</div>
      <div className={`auth-field__control${value.length > 0 ? ' is-filled' : ''}`}>
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onEnter) onEnter()
          }}
          className="auth-field__input"
        />
      </div>
    </div>
  )
}

function CaSocial({ label, glyph, onClick }: { label: string; glyph: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="hs-press hs-press--paper auth-social-button">
      {glyph}
      {label}
    </button>
  )
}

function FamilyCard({ name }: { name: string }) {
  return (
    <div className="auth-family-card">
      <div className="auth-family-card__shadow" />
      <div className="auth-family-card__back" />
      <div className="auth-family-card__body">
        <svg width="120" height="120" viewBox="0 0 120 120" className="auth-family-card__watermark" aria-hidden="true">
          <circle cx="60" cy="60" r="30" fill="var(--ink)" />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2
            return <line key={i} x1={60 + Math.cos(a) * 36} y1={60 + Math.sin(a) * 36} x2={60 + Math.cos(a) * 48} y2={60 + Math.sin(a) * 48} stroke="var(--ink)" strokeWidth="3" strokeLinecap="round" />
          })}
        </svg>
        <div className="auth-family-card__top">
          <span className="auth-family-card__mark" />
          <span className="auth-family-card__brand">Hopscotch</span>
          <span className="auth-family-card__label">Family card</span>
        </div>
        <div className="auth-eyebrow auth-family-card__member-label">Member</div>
        <div className="auth-family-card__name-line">
          <span className={`auth-family-card__name${name ? ' has-name' : ''}`}>
            {name || 'your name'}
          </span>
        </div>
        <div className="auth-family-card__footer">
          <div>
            <div className="auth-eyebrow auth-family-card__since-label">Member since</div>
            <div className="auth-family-card__year">2026</div>
          </div>
          <div className="auth-family-card__seal">
            <div className="auth-family-card__seal-ring" />
            <div className="auth-family-card__seal-copy">MEMBER</div>
          </div>
        </div>
      </div>
    </div>
  )
}

const GoogleGlyph = (
  <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 009 18z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 010-3.44V4.95H.96a9 9 0 000 8.1l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 00.96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
)

const AppleGlyph = (
  <svg width="15" height="17" viewBox="0 0 14 17" fill="var(--ink)" aria-hidden="true">
    <path d="M11.6 8.9c0-1.9 1.5-2.8 1.6-2.9-.9-1.3-2.2-1.5-2.7-1.5-1.1-.1-2.2.7-2.8.7-.6 0-1.5-.6-2.4-.6C3.6 4.6 2.3 5.3 1.6 6.5c-1.3 2.3-.3 5.7.9 7.5.6.9 1.4 1.9 2.3 1.9.9 0 1.3-.6 2.4-.6 1.1 0 1.4.6 2.4.6 1 0 1.6-.9 2.2-1.8.7-1 1-2 1-2-.1 0-1.9-.7-1.9-2.9zM9.8 3.2c.5-.6.8-1.4.7-2.2-.7 0-1.6.5-2.1 1.1-.5.5-.9 1.4-.8 2.2.8 0 1.6-.4 2.2-1.1z" />
  </svg>
)

export function CreateAccountPage() {
  const { loginWithRedirect } = useAuth0()
  const navigate = useNavigate()
  const code = sessionStorage.getItem(INVITE_CODE_KEY)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!code) {
    return <Navigate to="/auth/signup" replace />
  }

  const ready = name.trim().length > 0 && /.+@.+\..+/.test(email)

  const sendMagicLink = async () => {
    if (!ready || submitting) return
    setSubmitting(true)
    setError(null)
    setPendingFirstName(name.trim())

    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: PASSWORDLESS_CONNECTION,
          login_hint: email.trim(),
        },
      })
    } catch {
      setError('We could not start sign-up right now. Please try again.')
      setSubmitting(false)
    }
  }

  const social = (connection: string) => {
    if (name.trim()) setPendingFirstName(name.trim())
    loginWithRedirect({ authorizationParams: { connection, screen_hint: 'signup' } })
  }

  return (
    <div className="hs-brand auth-page auth-create-account">
      <Grain opacity={0.06} />

      <div className="auth-page__topbar auth-page__topbar--split">
        <div className="auth-brand-lockup">
          <span className="auth-brand-mark" />
          <span className="auth-brand-name">Hopscotch</span>
        </div>
        <div className="auth-returning">
          Already have an account?{' '}
          <button type="button" onClick={() => navigate('/auth/login')} className="auth-link-button">
            Sign in
          </button>
        </div>
      </div>

      <div className="auth-page__spread auth-page__spread--create">
        <div className="auth-page__spread-left auth-page__spread-left--create">
          <div className="auth-eyebrow auth-page__eyebrow">Code accepted - create your account</div>
          <h1 className="auth-heading auth-heading--create">
            Make it{' '}
            <span className="auth-highlight">
              <span className="auth-highlight__mark auth-highlight__mark--create" />
              <span className="auth-highlight__text">yours</span>
            </span>
            .
          </h1>
          <p className="auth-copy auth-copy--create">
            This is your grown-up account. You&rsquo;ll search books, manage your readers, and create literacy games.
          </p>

          <div className="auth-form-stack">
            <CaField label="Your first name" value={name} onChange={setName} placeholder="Sam" autoComplete="given-name" onEnter={sendMagicLink} /> {/* ph-safe: input value, maskAllInputs covers it */}
            <CaField label="Email" type="email" value={email} onChange={setEmail} placeholder="you@email.com" autoComplete="email" onEnter={sendMagicLink} />
          </div>

          {error ? <p className="auth-error auth-error--create">{error}</p> : null}

          <div className="auth-action-row auth-action-row--create">
            <button
              type="button"
              onClick={sendMagicLink}
              disabled={!ready || submitting}
              className={`auth-submit-button auth-submit-button--create${ready ? ' hs-press' : ' is-disabled'}`}
            >
              {submitting ? 'Sending code...' : 'Create account'}
              {!submitting && ARROW}
            </button>
            <span className="auth-terms">
              By continuing you agree to our{' '}
              <a href="/legal/terms" target="_blank" rel="noopener noreferrer">
                Terms
              </a>{' '}
              &amp;{' '}
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </span>
          </div>

          <p className="auth-passwordless-note">
            No password to set or remember. We&rsquo;ll email you a secure code to sign in.
          </p>

          <div className="auth-social-divider">
            <span />
            <strong>or continue with</strong>
            <span />
          </div>
          <div className="auth-social-row">
            <CaSocial label="Google" glyph={GoogleGlyph} onClick={() => social('google-oauth2')} />
            <CaSocial label="Apple" glyph={AppleGlyph} onClick={() => social('apple')} />
          </div>
        </div>

        <div className="auth-page__spread-right">
          <FamilyCard name={name.trim()} />
        </div>
      </div>
    </div>
  )
}
